import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, first, map } from 'rxjs';
import { FlextimeCorrectionType, ICreateFlexTimeCorrectionRequest, IFlexTimeCorrection } from '../models/flex-time-correction';
import { FlexTimeCorrectionService } from '../services/flex-time-correction.service';
import { IEmployee } from 'src/app/employee/employee';
import { enumToArray } from 'src/app/shared/helpers/enumToArray';
import { masketTextBoxValidator } from 'src/app/shared/validators/masketTextBox.validator';
import { TimeSpan } from 'src/app/shared/helpers/time-span';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { Store, select } from '@ngrx/store';
import { activeEmployeesSelector, getEmployees } from 'src/app/stores/global/employees/employees.store';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { translate } from '@jsverse/transloco';

// Which balance a correction applies to. Kept as strings in the form: the server field is the
// boolean affectsOvertime, which is mapped on load and on save.
type CorrectionTarget = 'flexTime' | 'overtime';

@Component({
    selector: 'app-flex-time-correction-grid',
    templateUrl: './flex-time-correction-grid.component.html',
    styleUrls: ['./flex-time-correction-grid.component.css'],
    standalone: false
})
export class FlexTimeCorrectionGridComponent implements OnInit {

  constructor(
    private route : ActivatedRoute,
    private flexTimeCorrectionService: FlexTimeCorrectionService,
    private store: Store<AppStateInterface>,
    private tenantSettingsStore: TenantSettingsStoreService
  ) {
    this.store.dispatch(getEmployees());
   }

  flexTimeCorrections: IFlexTimeCorrection [] = [];
  public formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;
  // Up to 999 hours; the server stores a TimeSpan, so there is no limit on that side.
  editMask = "ABC:DE";
  // Value of an untouched masked textbox (prompt placeholder is a space), used to detect "empty".
  private readonly emptyMaskValue = "   :  ";
  maskRules: { [key: string]: RegExp } = {
    A: /[0-9]+/,
    B: /[0-9]+/,
    C: /[0-9]+/,
    D: /[0-5]+/,
    E: /[0-9]+/,
  };

  flextimeCorrectionTypes = enumToArray(
    FlextimeCorrectionType,
    "flexTimeCorrection.flextimeCorrectionType."
  );

  get flexTimeCorrectionType(){
    return FlextimeCorrectionType;
  }

  affectsOptions: { id: CorrectionTarget, name: string }[] = [
    { id: 'flexTime', name: translate('flexTimeCorrection.affectsOvertimeOff') },
    { id: 'overtime', name: translate('flexTimeCorrection.affectsOvertimeOn') }
  ];

  public employees$ : Observable<IEmployee[]> =
  this.store.pipe(select(activeEmployeesSelector));

  public tenantSettings$ = this.tenantSettingsStore.tenantSettings$;

  ngOnInit(): void {
    this.flexTimeCorrections = this.route.snapshot.data.flexTimeCorrections as IFlexTimeCorrection[];
  }

  updateList(){
    this.flexTimeCorrectionService.searchflextimecorrections({pageNumber: 0, pageSize: 999999})
    .pipe(
      first(),
      map(data => {
        const response = data;
        response.data.map(corrections => {
          corrections.hours = TimeSpan.parse(corrections.hours).toStringShort();
        })

        return response;
      })
      )
    .subscribe(response => {
      this.flexTimeCorrections = response.data;
    })
  }

  createFormGroup(data: IFlexTimeCorrection = null){
    this.formGroup = new UntypedFormGroup({
      date: new UntypedFormControl(data?.date ?? new Date(), Validators.required),
      hours: new UntypedFormControl(data?.hours ?? null, 
        {validators: [Validators.required, masketTextBoxValidator(this.emptyMaskValue)],
        updateOn:"blur"}),
      employeeId: new UntypedFormControl(data?.employeeId ?? null, Validators.required),
      description: new UntypedFormControl(data?.description ?? null),
      flexTimeCorrectionType: new UntypedFormControl(data?.flexTimeCorrectionType ?? null, Validators.required),
      affects: new UntypedFormControl(data?.affectsOvertime ? 'overtime' : 'flexTime', Validators.required)
    });

    this.onAffectsChange(this.formGroup.get('affects').value);
  }

  // The server rejects an overtime correction without a remark, so require it here already.
  onAffectsChange(affects: CorrectionTarget): void {
    const control = this.formGroup.get('description');

    control.setValidators(affects === 'overtime' ? [Validators.required] : []);
    control.updateValueAndValidity();
  }

  addHandler({ sender }){
    this.closeEditor(sender);

    this.createFormGroup();

    sender.addRow(this.formGroup);
  }

  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  public saveHandler(event) {
    const { affects, ...formValue } = event.formGroup.value;
    const request: ICreateFlexTimeCorrectionRequest = {
      ...formValue,
      hours: TimeSpan.parse(formValue.hours).toString(),
      affectsOvertime: affects === 'overtime'
    }
    if (!event.dataItem.id){
      this.flexTimeCorrectionService.createflextimecorrection(request)
      .subscribe(response => {
        this.updateList();
      });
    } 

    event.sender.closeRow(event.rowIndex);
    
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  deletionDialogOpened = false;
  private itemToDelete: { id: string } | null = null;

  public removeHandler({ dataItem }) {
    this.itemToDelete = dataItem;
    this.deletionDialogOpened = true;
  }

  public deleteConfirmed() {
    const dataItem = this.itemToDelete;
    this.itemToDelete = null;
    if (!dataItem) return;
    this.flexTimeCorrectionService.deleteflextimecorrection(dataItem.id)
    .subscribe(response => {
      this.updateList();
    });;
  }


}
